import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BookingRuleCatchUpPreview, BookingRuleCatchUpBatchResult } from "@shared/booking-rule-catch-up";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function BookingRuleCatchUpDialog({
  ruleId, preview, onFinish,
}: { ruleId: number; preview: BookingRuleCatchUpPreview; onFinish: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState({ queued: 0, skipped: 0, remaining: preview.count });
  const [error, setError] = useState("");
  const queueCatchUp = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      setError("");
      let totals = { ...progress };
      while (totals.remaining > 0) {
        const response = await apiRequest("POST", `/api/app/messaging-rules/${ruleId}/booking-catch-up`, { token: preview.token });
        const batch: BookingRuleCatchUpBatchResult = await response.json();
        totals = {
          queued: totals.queued + batch.queued,
          skipped: totals.skipped + batch.skipped,
          remaining: batch.remaining,
        };
        setProgress(totals);
        if (batch.failed > 0) return { ...totals, failed: batch.failed };
      }
      return { ...totals, failed: 0 };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/messages"] });
      if (result.failed > 0) {
        setError(`${result.failed} request${result.failed === 1 ? "" : "s"} could not be queued. You can retry the remaining requests; already queued messages will not be repeated.`);
        return;
      }
      toast({
        title: "Booking-request catch-up complete",
        description: `${result.queued} request${result.queued === 1 ? "" : "s"} queued using this rule’s settings.${result.skipped ? ` ${result.skipped} skipped because they were already messaged or no longer eligible.` : ""}`,
      });
      onFinish();
    },
    onError: (err: unknown) => setError(parseErrorMessage(err, "Couldn't queue the remaining requests. Please try again.")),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !queueCatchUp.isPending) onFinish(); }}>
      <DialogContent
        data-testid="dialog-booking-rule-catch-up"
        onEscapeKeyDown={(event) => { if (queueCatchUp.isPending) event.preventDefault(); }}
        onInteractOutside={(event) => { if (queueCatchUp.isPending) event.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Send to existing booking requests?</DialogTitle>
          <DialogDescription>
            Your rule is saved. {preview.count} pending booking request{preview.count === 1 ? "" : "s"} at the newly added properties
            {preview.count === 1 ? " has" : " have"} not had a booking-request rule message queued.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground" data-testid="text-booking-catch-up-properties">
            {preview.propertyNames.slice(0, 5).join(", ")}
            {preview.propertyNames.length > 5 ? ` and ${preview.propertyNames.length - 5} more` : ""}
          </p>
          <p>
            {preview.approval === "review_before_sending"
              ? "These messages will be queued for your review, not sent automatically."
              : "These messages will be queued to send automatically."}
            {preview.delayMinutes ? ` The rule’s ${preview.delayMinutes}-minute delay starts when each message is queued.` : ""}
          </p>
          <p className="text-muted-foreground">
            Only the requests counted here will be considered, including older missed requests. Already messaged,
            closed, or moved requests are checked again and skipped. Normal automatic retries continue even if you choose “Not now.”
          </p>
          {(queueCatchUp.isPending || progress.queued > 0 || progress.skipped > 0) && (
            <p role="status" aria-live="polite" data-testid="text-booking-catch-up-progress">
              {progress.queued} queued, {progress.skipped} skipped, {progress.remaining} remaining.
            </p>
          )}
          {error && <p role="alert" className="text-destructive" data-testid="text-booking-catch-up-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFinish} disabled={queueCatchUp.isPending} data-testid="button-skip-booking-catch-up">
            Not now
          </Button>
          <Button onClick={() => queueCatchUp.mutate()} disabled={queueCatchUp.isPending} data-testid="button-confirm-booking-catch-up">
            {queueCatchUp.isPending ? "Queuing messages…" : error ? "Retry remaining requests" : `Queue ${preview.count} message${preview.count === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
